/**
 * HeroCTA - Ecosystem tiles for Home. Tuned for split layout (sidebar + main + Emma): compact, no redundant logo hero.
 */

import React from 'react';
import {
  Sparkles,
  Users,
  UsersRound,
  Truck,
  ArrowRight,
  Info,
  AlertTriangle,
  FileText,
  Target,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../design-system/components/Button/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/Tooltip';

interface Warning {
  type: string;
  message: string;
  link?: string;
}

interface HeroCTAProps {
  onExploreTemplates?: () => void;
  clientCount?: number;
  partnerCount?: number;
  vendorCount?: number;
  solutionCount?: number;
  icpCount?: number;
  documentCount?: number;
  warnings?: Warning[];
  lastUpdated?: string | null;
  isLoading?: boolean;
}

type EntityTileKey =
  | 'clients'
  | 'partners'
  | 'vendors'
  | 'solutions'
  | 'icps'
  | 'documents';

interface EntityTileDef {
  key: EntityTileKey;
  label: string;
  shortDescription: string;
  tooltip: string;
  icon: React.ReactNode;
  /** Navigate target */
  path: string;
  /** When path is /data-foundation, optional wizard step (0–4) */
  entityManagerStep?: number;
  getCount: (p: {
    clientCount: number;
    partnerCount: number;
    vendorCount: number;
    solutionCount: number;
    icpCount: number;
    documentCount: number;
  }) => number;
}

const ENTITY_TILES: EntityTileDef[] = [
  {
    key: 'clients',
    label: 'Clients',
    shortDescription: 'Sell-to accounts',
    tooltip:
      'End customers and accounts in your ecosystem. Used in campaigns to anchor messaging and offers. Opens Entity Manager on the business entities step.',
    icon: <Users className="h-4 w-4 text-emerald-600" aria-hidden />,
    path: '/data-foundation',
    entityManagerStep: 0,
    getCount: (p) => p.clientCount,
  },
  {
    key: 'partners',
    label: 'Partners',
    shortDescription: 'Co-sell partners',
    tooltip:
      'Resellers, integrators, and strategic partners you work with. Manage them alongside clients in Entity Manager.',
    icon: <UsersRound className="h-4 w-4 text-emerald-600" aria-hidden />,
    path: '/data-foundation',
    entityManagerStep: 0,
    getCount: (p) => p.partnerCount,
  },
  {
    key: 'vendors',
    label: 'Vendors',
    shortDescription: 'Suppliers & tech',
    tooltip:
      'Vendors and suppliers linked to your solutions. Configure them in Entity Manager so campaigns stay accurate.',
    icon: <Truck className="h-4 w-4 text-emerald-600" aria-hidden />,
    path: '/data-foundation',
    entityManagerStep: 0,
    getCount: (p) => p.vendorCount,
  },
  {
    key: 'solutions',
    label: 'Solutions',
    shortDescription: 'Offers you promote',
    tooltip:
      'Offerings you promote in campaigns. Define features, positioning, and links to partners and vendors.',
    icon: <Sparkles className="h-4 w-4 text-emerald-600" aria-hidden />,
    path: '/data-foundation',
    entityManagerStep: 1,
    getCount: (p) => p.solutionCount,
  },
  {
    key: 'icps',
    label: 'ICPs',
    shortDescription: 'Target segments',
    tooltip:
      'Target segments: industries, company size, and buying signals. Campaigns use ICPs to tailor tone and channels.',
    icon: <Target className="h-4 w-4 text-emerald-600" aria-hidden />,
    path: '/data-foundation',
    entityManagerStep: 2,
    getCount: (p) => p.icpCount,
  },
  {
    key: 'documents',
    label: 'Documents',
    shortDescription: 'Files for AI context',
    tooltip:
      'Uploads and knowledge sources that ground AI outputs. Opens Upload Center to add or manage files.',
    icon: <FileText className="h-4 w-4 text-emerald-600" aria-hidden />,
    path: '/upload-center',
    getCount: (p) => p.documentCount,
  },
];

export const HeroCTA: React.FC<HeroCTAProps> = ({
  onExploreTemplates,
  clientCount = 0,
  partnerCount = 0,
  vendorCount = 0,
  solutionCount = 0,
  icpCount = 0,
  documentCount = 0,
  warnings = [],
  lastUpdated = null,
  isLoading = false,
}) => {
  const navigate = useNavigate();
  const counts = {
    clientCount,
    partnerCount,
    vendorCount,
    solutionCount,
    icpCount,
    documentCount,
  };
  const hasEntities =
    clientCount > 0 || partnerCount > 0 || vendorCount > 0 || solutionCount > 0;
  const isFirstTime =
    clientCount === 0 &&
    partnerCount === 0 &&
    vendorCount === 0 &&
    solutionCount === 0;
  const activeWarnings = warnings.filter((w) => w.type !== 'error');

  const goTile = (tile: EntityTileDef) => {
    if (tile.path === '/data-foundation' && tile.entityManagerStep !== undefined) {
      navigate(tile.path, { state: { entityManagerStep: tile.entityManagerStep } });
    } else {
      navigate(tile.path);
    }
  };

  return (
    <TooltipProvider delayDuration={280}>
      <section
        className="mb-5 overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-emerald-950/[0.04]"
        aria-label="Ecosystem counts"
      >
        <div
          className="-mx-4 -mt-4 mb-3 h-0.5 bg-gradient-to-r from-emerald-500/90 via-teal-500/80 to-emerald-600/90"
          aria-hidden
        />
        <div className="mb-3 border-b border-emerald-100/80 pb-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-emerald-800/80">Ecosystem</h2>
          <p className="mt-0.5 max-w-md text-xs leading-snug text-slate-500">
            Tile → Entity Manager or Upload. Hover a tile for details.
          </p>
        </div>

        {isFirstTime && (
          <div className="mb-3 rounded-md border border-emerald-100 bg-emerald-50/40 px-3 py-2">
            <div className="flex items-start gap-2">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
              <p className="text-xs leading-relaxed text-slate-700">
                Add clients, partners, solutions, and ICPs first (~5 min). Then campaigns unlock.
              </p>
            </div>
          </div>
        )}

        {/* 2×3 grid: readable beside sidebar + Emma (no six-across squeeze) */}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          {ENTITY_TILES.map((tile) => {
            const n = tile.getCount(counts);
            return (
              <Tooltip key={tile.key}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => goTile(tile)}
                    disabled={isLoading}
                    aria-label={`${tile.label}: ${tile.shortDescription}. Opens related screen.`}
                    className="group flex w-full min-h-[4.5rem] flex-col rounded-md border border-slate-200/90 bg-slate-50/40 p-2.5 text-left transition hover:border-emerald-300 hover:bg-white hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-emerald-500 disabled:cursor-wait disabled:opacity-60"
                  >
                    <div className="flex items-start justify-between gap-1.5">
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                          {tile.label}
                        </p>
                        <p className="mt-0.5 font-mono text-lg font-bold tabular-nums leading-none text-emerald-900">
                          {isLoading ? '…' : n}
                        </p>
                        <p className="mt-1 truncate text-[10px] text-slate-500" title={tile.shortDescription}>
                          {tile.shortDescription}
                        </p>
                      </div>
                      <div className="shrink-0 rounded-md bg-white p-1 ring-1 ring-slate-200/80 group-hover:ring-emerald-200">
                        {tile.icon}
                      </div>
                    </div>
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="max-w-xs border-slate-200 bg-white text-slate-800 shadow-md"
                >
                  <p className="text-xs leading-relaxed">{tile.tooltip}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        {!isLoading && hasEntities && lastUpdated && (
          <p className="mt-3 border-t border-slate-100 pt-2 text-right text-[10px] text-slate-400">
            Counts last updated{' '}
            {new Date(lastUpdated).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
        )}

        {!isLoading && hasEntities && activeWarnings.length > 0 && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/90 p-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-amber-900">
                {activeWarnings.map((w, i) => (
                  <span key={i} className="flex items-center gap-1">
                    {w.link ? (
                      <button
                        type="button"
                        onClick={() => navigate(w.link!)}
                        className="underline decoration-amber-700/50 underline-offset-2 hover:text-amber-950"
                      >
                        {w.message}
                      </button>
                    ) : (
                      w.message
                    )}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {isFirstTime && (
          <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5 text-xs text-slate-600">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white">
              1
            </span>
            <span>Setup</span>
            <ArrowRight className="h-3 w-3 text-slate-400" />
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600">
              2
            </span>
            <span>Campaign</span>
            <ArrowRight className="h-3 w-3 text-slate-400" />
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600">
              3
            </span>
            <span>Launch</span>
          </div>
        )}

        {onExploreTemplates && (
          <div className="mt-3 flex justify-center border-t border-slate-100 pt-3">
            <Button
              variant="outline"
              onClick={onExploreTemplates}
              disabled={isLoading}
              className="h-8 border-slate-200 text-xs text-slate-600 hover:bg-slate-50"
            >
              Explore templates
            </Button>
          </div>
        )}
      </section>
    </TooltipProvider>
  );
};

export default HeroCTA;
